import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom-v5-compat";
import { useFeatureFlag, FeatureFlagName } from "@apac/feature-flags";
import { GetSessionInfoResponseEnum } from "@b2bportal/auth-api";
import { type ProductInfo, TravelCategory } from "@b2bportal/chat-api";
import { fetchSessionInfo } from "@b2bportal/core-auth";
import { datadogRum } from "@datadog/browser-rum";
import { ChatFab } from "@hopper-b2b/chat";
import { useI18nContext } from "@hopper-b2b/i18n";
import { PATH_TRIPS } from "@hopper-b2b/utilities";
import { CUSTOMER_SUPPORT_PATH, INVALID_SESSION_PATH } from "@hsbc/utilities";
import { Loading } from "@tenant/ui";
import { useAuth } from "../../hooks";

import "./styles.scss";

export const flightItineraryRoute = (reservationId: string) =>
  `flights/${reservationId}`;

export const hotelItineraryRoute = (reservationId: string) =>
  `hotels/${reservationId}`;

export const carItineraryRoute = (reservationId: string) =>
  `cars/${reservationId}`;

export const ProtectedRoute = () => {
  const { isLoggedIn, login, setUserInfo } = useAuth();
  const copyConversationIdEnabled = useFeatureFlag(
    FeatureFlagName.copy_conversation_id
  );
  const navigate = useNavigate();

  const { brand } = useI18nContext();
  const showChat: boolean = brand.showAIChat ?? true;

  useEffect(() => {
    const tryGetSessionInfo = async () => {
      try {
        const res = await fetchSessionInfo();
        if (res.GetSessionInfoResponse === GetSessionInfoResponseEnum.Session) {
          const { userInfo } = res.sessionInfo;

          const hopperUserId = userInfo.partnerMetadata.hopperUserId;
          if (hopperUserId != null) {
            datadogRum.setUser({ id: hopperUserId });
          }
          setUserInfo({
            ...userInfo,
            phoneNumber: userInfo.phone,
            hopperUserId,
          });
          login();
        } else {
          console.log("fetch session rejected:", res.GetSessionInfoResponse);
          handleInvalidSession();
        }
      } catch (error) {
        console.log("error fetching session info:", error);
        handleInvalidSession();
      }
    };

    const handleInvalidSession = () => {
      navigate(INVALID_SESSION_PATH);
    };

    tryGetSessionInfo();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return isLoggedIn ? (
    <>
      {showChat && (
        <ChatFab
          copyConversationIdEnabled={copyConversationIdEnabled}
          hidePaths={[CUSTOMER_SUPPORT_PATH]}
          showPaths={[PATH_TRIPS]}
          getTripDetailsPath={(productInfo: ProductInfo) => {
            switch (productInfo.travelCategory) {
              case TravelCategory.CARS:
                return `${PATH_TRIPS}${carItineraryRoute(productInfo.itineraryUuid)}`;
              case TravelCategory.FLIGHTS:
                return `${PATH_TRIPS}${flightItineraryRoute(productInfo.itineraryUuid)}`;
              case TravelCategory.HOTELS:
                return `${PATH_TRIPS}${hotelItineraryRoute(productInfo.itineraryUuid)}`;
              default:
                console.log(
                  `Unsupported travel category: ${productInfo.travelCategory}`
                );
                return PATH_TRIPS;
            }
          }}
        />
      )}
      <Outlet />
    </>
  ) : (
    <div className="protected-route-root">
      <Loading />
    </div>
  );
};
